// components/ui/NotificationsDropdown.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  IconBell,
  IconX,
  IconAlertCircle,
  IconInfoCircle,
  IconCircleCheck,
  IconChevronRight,
  IconUsers,
  IconCreditCard,
  IconRocket,
  IconServer,
  IconSettings,
  IconEye,
  IconUserPlus,
  IconCashBanknote,
  IconHeadset,
} from "@tabler/icons-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";

const NotificationsDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
  } = useNotifications();

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Agrupar notificaciones por categoría (solo las últimas 2-3 por módulo)
  const getGroupedNotifications = () => {
    const grouped: Record<string, any[]> = {};

    // Filtrar solo las no leídas y las más recientes
    const sorted = [...notifications].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    // Agrupar por categoría
    sorted.forEach((notification) => {
      const category = notification.category || "sistema";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      // Solo mostrar 2 por categoría en el dropdown
      if (grouped[category].length < 2) {
        grouped[category].push(notification);
      }
    });

    return grouped;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, any> = {
      clientes: IconUsers,
      suscripciones: IconCreditCard,
      onboarding: IconRocket,
      plataforma: IconServer,
      sistema: IconSettings,
      registrations: IconUserPlus, // ✅ NUEVO
      pagos: IconCashBanknote, // ✅ NUEVO
      soporte: IconHeadset, // ✅ NUEVO
    };
    return icons[category as keyof typeof icons] || IconBell;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      clientes: "Clientes",
      suscripciones: "Suscripciones",
      onboarding: "Onboarding",
      plataforma: "Plataforma",
      sistema: "Sistema",
      registrations: "Registrados", // ✅ NUEVO
      pagos: "Pagos", // ✅ NUEVO
      soporte: "Soporte", // ✅ NUEVO
    };
    return labels[category as keyof typeof labels] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      clientes: "border-brand-primary/20 bg-brand-soft",
      suscripciones: "border-state-success/20 bg-state-success-soft",
      onboarding: "border-brand-primary/20 bg-brand-soft",
      plataforma: "border-state-info/20 bg-state-info-soft",
      sistema: "border-border bg-surface-secondary",
      registrations: "border-state-warning/20 bg-state-warning-soft",
      pagos: "border-state-success/20 bg-state-success-soft",
      soporte: "border-state-danger/20 bg-state-danger-soft",
    };
    return (
      colors[category as keyof typeof colors] || "border-border bg-surface-secondary"
    );
  };

  const getCategoryRoute = (category: string) => {
    const routes: Record<string, string> = {
      clientes: "/clientes",
      suscripciones: "/suscripciones",
      onboarding: "/onboarding",
      plataforma: "/plataforma/estado",
      sistema: "/configuracion",
      registrations: "/clientes", 
      pagos: "/pagos", 
      soporte: "/soporte", 
    };
    return routes[category as keyof typeof routes] || "/";
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "error":
        return <IconAlertCircle className="w-4 h-4 text-state-danger" />;
      case "warning":
        return <IconAlertCircle className="w-4 h-4 text-state-warning" />;
      case "success":
        return <IconCircleCheck className="w-4 h-4 text-state-success" />;
      default:
        return <IconInfoCircle className="w-4 h-4 text-state-info" />;
    }
  };

  const groupedNotifications = getGroupedNotifications();
  const categories = Object.keys(groupedNotifications);

  // En el handleNavigateToModule, pasa un estado extra
  const handleNavigateToModule = (category: string) => {
    const route = getCategoryRoute(category);
    
    // Si es la categoría 'registrations', pasa un estado para abrir el modal
    if (category === 'registrations') {
      navigate(route, { state: { openRegistrationsModal: true } });
    } else {
      navigate(route);
    }
    
    setIsOpen(false);
    setActiveCategory(null);
  };

  // Navegar a página de notificaciones
  const handleViewAll = () => {
    navigate("/notificaciones");
    setIsOpen(false);
    setActiveCategory(null);
  };

  return (
    <div className="relative ui-tokens" ref={dropdownRef}>
      {/* Botón de notificaciones */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setActiveCategory(null);
        }}
        className="p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all relative group"
      >
        <IconBell className="w-5 h-5 group-hover:shake" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 border-2 border-white dark:border-gray-900 shadow-sm animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {isOpen && (
        <div
          className="fixed top-16 left-3 right-3 sm:absolute sm:top-full sm:mt-2 sm:left-auto sm:right-0 sm:w-[400px] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border
          border-gray-200 dark:border-gray-700 z-50 flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                Notificaciones
              </h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
                  {unreadCount} nuevas
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => {
                    markAllAsRead();
                  }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                >
                  Marcar todas
                </button>
              )}
              <button
                onClick={handleViewAll}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
              >
                <IconEye className="w-3 h-3" />
                Ver todas
              </button>
            </div>
          </div>

          {/* Contenido - Notificaciones por módulo */}
          <div className="flex-1 overflow-y-auto max-h-[450px] p-2 space-y-3">
            {categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
                <IconBell className="w-12 h-12 mb-2 opacity-20" />
                <p className="text-sm font-medium">No hay notificaciones</p>
                <p className="text-xs">
                  Todas tus notificaciones aparecerán aquí
                </p>
              </div>
            ) : (
              categories.map((category) => {
                const CategoryIcon = getCategoryIcon(category);
                const categoryNotifications = groupedNotifications[category];
                const hasUnread = categoryNotifications.some((n) => !n.read);

                return (
                  <div key={category} className="space-y-1.5">
                    {/* Encabezado del módulo */}
                    <div className="flex items-center justify-between px-2 py-1">
                      <button
                        onClick={() => handleNavigateToModule(category)}
                        className="flex items-center gap-2 group hover:bg-gray-100 dark:hover:bg-gray-700/50 px-2 py-1 rounded-lg transition-colors"
                      >
                        <CategoryIcon className="w-4 h-4 text-gray-500 group-hover:text-blue-500 transition-colors" />
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {getCategoryLabel(category)}
                        </span>
                        {hasUnread && (
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        )}
                        <IconChevronRight className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                      <button
                        onClick={() => handleNavigateToModule(category)}
                        className="text-[10px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                      >
                        Ver todo en {getCategoryLabel(category)}
                      </button>
                    </div>

                    {/* Notificaciones de este módulo */}
                    <div
                      className={`rounded-lg border ${getCategoryColor(category)} p-1.5 space-y-1`}
                    >
                      {categoryNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`
                            group flex items-start gap-2.5 px-3 py-2 rounded-lg transition-all cursor-pointer
                            ${!notification.read ? "bg-blue-50/30 dark:bg-blue-500/5" : "hover:bg-gray-50 dark:hover:bg-gray-700/30"}
                          `}
                          onClick={() => {
                            markAsRead(notification.id);
                            // Registrados vive en un modal dentro de Clientes
                            if (category === "registrations") {
                              handleNavigateToModule(category);
                            } else {
                              navigate(notification.link || getCategoryRoute(category));
                            }
                            setIsOpen(false);
                          }}
                        >
                          {/* Icono de tipo */}
                          <div className="flex-shrink-0 mt-0.5">
                            {getIcon(notification.type)}
                          </div>

                          {/* Contenido */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                                {notification.title}
                              </p>
                              <span className="text-[9px] text-gray-400 flex-shrink-0">
                                {new Date(
                                  notification.createdAt,
                                ).toLocaleTimeString("es-ES", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-1">
                              {notification.message}
                            </p>
                          </div>

                          {/* Botón cerrar */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearNotification(notification.id);
                            }}
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                          >
                            <IconX className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer - Acciones rápidas */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between">
            <button
              onClick={handleViewAll}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors flex items-center gap-1"
            >
              <IconEye className="w-3.5 h-3.5" />
              Ver todas las notificaciones
            </button>
            <span className="text-[10px] text-gray-400">
              {notifications.length} notificaciones totales
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
